import type { AuthMethod, AuthMode, Plan, SuperProperties, WorkspaceRole } from '@parcel/analytics';
import {
  VERIFY_FLAG_KEY,
  authMethodFromSupabaseProvider,
  internalBlockApplies,
  isInternal,
} from '@parcel/analytics';
import {
  capture as captureEvent,
  identify,
  initAnalytics,
  registerSuperProperties,
  shutdownAnalytics,
} from '@parcel/analytics/browser';
import { useQuery } from '@tanstack/react-query';
import { useRouterState } from '@tanstack/react-router';
import * as React from 'react';
import { useAuth } from './auth.js';
import { SIGNUP_REF_KEY } from './signup-intent.js';
import { isSupabaseConfigured } from './supabase.js';
import { getWorkspace, getWorkspaceSlugFromPath } from './workspaces.js';

const PRODUCTION_HOST = 'app.parcelengineering.com';

interface WorkspaceIdentity {
  id: string;
  slug: string;
  role: WorkspaceRole;
}

/** Pure so the loading-state rule is testable without React. */
export function buildSuperProperties(
  workspace: WorkspaceIdentity | null,
  plan: Plan,
  entitlementsLoading: boolean,
): Pick<SuperProperties, 'workspace_id' | 'workspace_slug' | 'plan' | 'workspace_role'> {
  if (!workspace) {
    return { workspace_id: null, workspace_slug: null, plan: null, workspace_role: null };
  }
  return {
    workspace_id: workspace.id,
    workspace_slug: workspace.slug,
    plan: entitlementsLoading ? null : plan,
    workspace_role: workspace.role,
  };
}

/**
 * Set at the OAuth click in signup-form.tsx, read back here after the redirect.
 * Per-tab, so it dies with the attempt rather than lingering like localStorage.
 */
export const OAUTH_INTENT_KEY = 'parcel:oauth-intent';

export { SIGNUP_REF_KEY } from './signup-intent.js';
// dossier:{template}:{slug}: lower-case tokens joined by at most three colons.
const SIGNUP_REF_RE = /^[a-z0-9_-]+(?::[a-z0-9_-]+){0,3}$/;

export function sanitizeSignupRef(value: string | null | undefined): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length <= 120 && SIGNUP_REF_RE.test(trimmed) ? trimmed : null;
}

// A dossier visit attributes the signup that follows it, not one hours later in the same tab.
export const SIGNUP_REF_TTL_MS = 2 * 60 * 60 * 1000;

function readStoredRef(storage: Storage, now: number): string | null {
  try {
    const value = JSON.parse(storage.getItem(SIGNUP_REF_KEY) ?? 'null') as {
      ref?: unknown;
      at?: unknown;
    } | null;
    if (!value || typeof value.ref !== 'string' || typeof value.at !== 'number') return null;
    return now - value.at > SIGNUP_REF_TTL_MS ? null : sanitizeSignupRef(value.ref);
  } catch {
    return null;
  }
}

/** Remember the URL's ref for the tab; answer the ref in force (the URL's, else a fresh stored one). */
export function storeSignupRef(
  search: string,
  storage: Storage = window.sessionStorage,
  now = Date.now(),
): string | null {
  const fromUrl = sanitizeSignupRef(new URLSearchParams(search).get('ref'));
  try {
    if (fromUrl) storage.setItem(SIGNUP_REF_KEY, JSON.stringify({ ref: fromUrl, at: now }));
  } catch {
    // Attribution is never worth a broken signup page.
  }
  return fromUrl ?? readStoredRef(storage, now);
}

/** Read and clear: a completed signup is the last event the ref belongs to. */
export function takeSignupRef(
  storage: Storage = window.sessionStorage,
  now = Date.now(),
): string | null {
  const ref = readStoredRef(storage, now);
  try {
    storage.removeItem(SIGNUP_REF_KEY);
  } catch {
    // A ref that cannot be cleared lapses on its own.
  }
  return ref;
}

/** The optional ref property, present only when there is one. */
export function withRef<T extends object>(properties: T, ref: string | null): T & { ref?: string } {
  return ref ? { ...properties, ref } : properties;
}

/**
 * Pure, because the interesting part is the rejection cases, not the happy path.
 * OAuth is a full-page redirect, so the click site cannot fire signup_completed
 * itself, and "a session appeared" is equally true on every returning visit.
 * The stored mode plus the session's own provider is the smallest pair that
 * separates a completed OAuth signup from an ordinary login.
 */
export function resolveOAuthCompletion(
  storedMode: string | null,
  provider: string | undefined,
): { method: AuthMethod; mode: AuthMode } | null {
  if (storedMode !== 'signup' && storedMode !== 'signin') return null;
  const method = authMethodFromSupabaseProvider(provider);
  // Redirect providers only. A stale marker left by an abandoned OAuth attempt
  // must not claim a later email-OTP signup, which fires its own event.
  if (method !== 'microsoft' && method !== 'google') return null;
  return { method, mode: storedMode };
}

export type ProviderAction = 'wait' | 'shutdown' | 'init';

/**
 * Pure, so the ordering rule is a test rather than a comment. `wait` must be
 * unreachable-past: while auth is loading nobody's email is known, so treating
 * that state as "not internal" would initialize before the internal block can
 * possibly apply, and initAnalytics cannot be taken back.
 *
 * The hatch-outranks-internal rule is `internalBlockApplies`, shared with
 * `resolveInitGate` in the package, so the two decisions cannot drift apart.
 */
export function resolveProviderAction(input: {
  authLoading: boolean;
  internal: boolean;
  verifyFlag: boolean;
}): ProviderAction {
  if (input.authLoading) return 'wait';
  if (internalBlockApplies(input)) return 'shutdown';
  return 'init';
}

export function AnalyticsProvider({ children }: { children: React.ReactNode }) {
  const { session, loading } = useAuth();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const workspaceSlug = getWorkspaceSlugFromPath(pathname);

  // Same query key as useEntitlements, so this is a cache hit rather than a
  // second request, and it carries id, slug, role and effectivePlan together.
  const workspaceQuery = useQuery({
    queryKey: ['workspace', workspaceSlug],
    enabled: Boolean(workspaceSlug) && isSupabaseConfigured,
    queryFn: () => getWorkspace(workspaceSlug ?? ''),
  });

  const userId = session?.user.id ?? null;
  const userEmail = session?.user.email ?? null;
  const internal = isInternal(userEmail);

  // initAnalytics is idempotent, which matters because StrictMode double-invokes.
  React.useEffect(() => {
    // Read from the same key initAnalytics reads, so the two cannot disagree.
    const action = resolveProviderAction({
      authLoading: loading,
      internal,
      verifyFlag: window.sessionStorage.getItem(VERIFY_FLAG_KEY) !== null,
    });

    // Do nothing until getSession() has resolved. Before that everyone looks
    // external, and initAnalytics is one-shot: initializing here would capture
    // the whole session, replay included, for an employee whose email arrives
    // milliseconds later. RootShell renders only a loading placeholder during
    // this window, so no user-visible interaction is missed.
    if (action === 'wait') return;

    // Normally a no-op, because initAnalytics has not run and gate rule 3 will
    // refuse it anyway. It does real work only when an employee signs in on a
    // tab that already initialized anonymously, which rule 3 arrives too late
    // for. See shutdownAnalytics for what that costs.
    if (action === 'shutdown') {
      shutdownAnalytics();
      return;
    }

    initAnalytics({
      key: import.meta.env.VITE_POSTHOG_KEY,
      apiHost: import.meta.env.VITE_POSTHOG_HOST,
      uiHost: 'https://us.posthog.com',
      isProductionBuild: import.meta.env.PROD,
      productionHost: PRODUCTION_HOST,
      userEmail,
    });
  }, [loading, internal, userEmail]);

  // `loading` gates this the same way it gates the init effect above. Supabase
  // delivers the session (via onAuthStateChange) a beat before getSession()
  // clears loading, so without the gate this effect fires while init is still
  // in its 'wait' branch: identify no-ops behind the wrapper's uninitialized
  // guard, and its deps never change again, so it never re-runs. Every user
  // stays anonymous. Keying on loading makes it re-run the moment init is live.
  React.useEffect(() => {
    if (loading || !userId || !userEmail) return;
    identify(userId, {
      email: userEmail,
      created_at: session?.user.created_at,
      // Mapped, not raw. app_metadata.provider is 'azure' for Microsoft, and an
      // unmapped person property would split the breakdown against the events,
      // which is exactly what the taxonomy mapping exists to prevent.
      auth_provider: authMethodFromSupabaseProvider(session?.user.app_metadata?.provider),
    });
  }, [loading, userId, userEmail, session?.user.created_at, session?.user.app_metadata?.provider]);

  // Declared after identify so the person exists before signup_completed lands.
  React.useEffect(() => {
    if (loading || !userId) return;
    const storedMode = window.sessionStorage.getItem(OAUTH_INTENT_KEY);
    if (storedMode === null) return;
    // Cleared before the capture, not after: a throw or a StrictMode re-run
    // would otherwise fire a second signup_completed for one signup.
    window.sessionStorage.removeItem(OAUTH_INTENT_KEY);
    const completion = resolveOAuthCompletion(storedMode, session?.user.app_metadata?.provider);
    if (completion) captureEvent('signup_completed', withRef(completion, takeSignupRef()));
  }, [loading, userId, session?.user.app_metadata?.provider]);

  const workspace = workspaceQuery.data;
  React.useEffect(() => {
    registerSuperProperties(
      buildSuperProperties(
        workspace ? { id: workspace.id, slug: workspace.slug, role: workspace.role } : null,
        workspace?.effectivePlan ?? 'free',
        workspaceQuery.isLoading,
      ),
    );
  }, [workspace, workspaceQuery.isLoading]);

  return <>{children}</>;
}

export function useAnalytics() {
  return React.useMemo(() => ({ capture: captureEvent }), []);
}

/**
 * Fire once per distinct key. A bare `useEffect` is wrong for capture: React
 * StrictMode mounts, runs effects, tears them down and runs them again in dev,
 * which is the build the verification hatch runs on, so every event would
 * arrive twice while someone is checking whether events arrive correctly.
 *
 * The guard is a ref because refs survive that remount and state does not.
 * A new key fires again on purpose: the prev/next arrows in a drawer swap the
 * record id without unmounting, and that is a second record viewed. Pass `null`
 * to mean "nothing to fire yet". The callback receives the non-null key, so a
 * call site never dereferences its own nullable prop inside the closure, which
 * strict TS cannot correlate with the key's null check.
 */
export function useCaptureOnce(key: string | null, fire: (key: string) => void): void {
  const lastFired = React.useRef<string | null>(null);
  const fireRef = React.useRef(fire);
  fireRef.current = fire;

  React.useEffect(() => {
    if (key === null || lastFired.current === key) return;
    lastFired.current = key;
    fireRef.current(key);
    // `fire` is deliberately not a dependency. Call sites pass an inline
    // closure, so it is a new function every render; the key is what decides
    // whether this should run.
  }, [key]);
}
